// Floating info windows: a per-selection Box/Sphere window plus the probe-point
// window opened from the hover tooltip's Info button.
//
// Each window registers with the WindowRegistry (Stage 1) so drag / resize /
// position persistence / the × close button come for free. Stats-only:
// editable controls live in the Selections tab, never here.
//
// Selection windows are NOT auto-opened on focus — a scene-side info button
// (planned) calls openSelectionInfo(id). The probe window is opened by the
// hover tooltip (openProbeInfo) and reflects the last probed point.
//
// Module pattern mirrors reductions/plotWindow.ts: the WindowRegistry + viewer
// are captured once at install time; callers just call open*().

import { store, params } from '@store/instance';
import { INTENTS } from '@store/intents';
import {
    coordSystemLabel,
    computeGlobalFieldStats,
    computeMaskedFieldStats,
    probeValueLabel,
} from './stats';
import type { BoxSelection, Selection, SelectionId, SphereSelection } from '@store/schema';
import type { ViewerContext } from '@app/context';
import type { WindowHandle, WindowRegistry } from '../windowManager';

interface WindowEntry {
    handle: WindowHandle;
    panel: HTMLElement;
    headerLabel: HTMLElement;
    body: HTMLElement;
    offs: Array<() => void>;
}

const windows = new Map<SelectionId, WindowEntry>();
let probeEntry: WindowEntry | null = null;
let registry: WindowRegistry | null = null;
let viewerRef: ViewerContext | null = null;

/** Capture the registry + viewer. Closes any open window when its selection is
 *  deleted; otherwise windows open only on explicit open*() calls. */
export function installSelectionInfo(viewer: ViewerContext, reg: WindowRegistry): () => void {
    registry = reg;
    viewerRef = viewer;
    const offDelete = store.onIntent(INTENTS.SELECTION_DELETE, (p) => {
        if (p?.id) closeSelectionInfo(p.id);
    });
    return () => {
        offDelete();
        for (const id of Array.from(windows.keys())) closeSelectionInfo(id);
        closeProbeInfo();
        registry = null;
        viewerRef = null;
    };
}

// ─── shared panel construction ───────────────────────────────────────────────

interface PanelParts {
    handle: WindowHandle;
    panel: HTMLElement;
    headerLabel: HTMLElement;
    body: HTMLElement;
}

function createPanel(domId: string, minWidth: number, minHeight: number): PanelParts {
    const panel = document.createElement('div');
    panel.id = domId;
    panel.className = 'selection-info-panel';
    // No inline `display`; `.selection-info-panel { display: none }` owns it so
    // the `.show` class can flip to flex at the same specificity.
    panel.style.right = '20px';
    panel.style.top = '90px';
    panel.style.width = '260px';

    const grip = document.createElement('div');
    grip.className = 'mv-window-grip';
    grip.setAttribute('aria-hidden', 'true');
    panel.appendChild(grip);

    const header = document.createElement('div');
    header.className = 'selection-info-header';
    const headerLabel = document.createElement('div');
    headerLabel.className = 'selection-info-title';
    headerLabel.textContent = '…';
    header.appendChild(headerLabel);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'selection-info-close';
    closeBtn.setAttribute('aria-label', 'Close info');
    closeBtn.innerHTML = '<i class="fas fa-times" aria-hidden="true"></i>';
    header.appendChild(closeBtn);
    panel.appendChild(header);

    const body = document.createElement('div');
    body.className = 'selection-info-body';
    panel.appendChild(body);

    const resize = document.createElement('div');
    resize.className = 'selection-info-resize';
    resize.dataset['noDrag'] = '';
    panel.appendChild(resize);

    document.body.appendChild(panel);

    const handle = registry!.register({
        id: domId,
        panel,
        header,
        resize,
        closeBtn,
        minWidth,
        minHeight,
    });

    return { handle, panel, headerLabel, body };
}

// ─── registered selection window ─────────────────────────────────────────────

/** Open or focus the floating info window for a registered Box/Sphere. No-op if
 *  not installed or the selection doesn't exist (e.g. the probe cube). */
export function openSelectionInfo(id: SelectionId): void {
    if (!registry || !viewerRef) return;
    if (!params.selections[id]) return;
    const existing = windows.get(id);
    if (existing) {
        existing.handle.show();
        existing.handle.raise();
        repaintSelection(existing, id);
        return;
    }
    const base = createPanel(`selection-info-${id}`, 220, 160);
    const repaint = (): void => {
        const entry = windows.get(id);
        if (entry) repaintSelection(entry, id);
    };
    const entry: WindowEntry = {
        ...base,
        offs: [
            store.subscribe(`params.selections.${id}`, repaint),
            store.subscribe('params.fieldComponent', repaint),
            store.subscribe('params.centerOnZero', repaint),
        ],
    };
    windows.set(id, entry);
    entry.handle.show();
    repaintSelection(entry, id);
}

export function closeSelectionInfo(id: SelectionId): void {
    const entry = windows.get(id);
    if (!entry) return;
    for (const off of entry.offs) { try { off(); } catch { /* swallow */ } }
    entry.handle.dispose();
    entry.panel.remove();
    windows.delete(id);
}

// ─── probe-point window ──────────────────────────────────────────────────────

/** Open or focus the probe-point info window (hover tooltip's Info button). */
export function openProbeInfo(): void {
    if (!registry || !viewerRef) return;
    if (probeEntry) {
        probeEntry.handle.show();
        probeEntry.handle.raise();
        repaintProbe(probeEntry);
        return;
    }
    const base = createPanel('selection-info-probe', 220, 160);
    const repaint = (): void => { if (probeEntry) repaintProbe(probeEntry); };
    probeEntry = {
        ...base,
        offs: [
            store.subscribe('selection', repaint),
            store.subscribe('params.fieldComponent', repaint),
            store.subscribe('params.centerOnZero', repaint),
        ],
    };
    probeEntry.handle.show();
    repaintProbe(probeEntry);
}

export function closeProbeInfo(): void {
    if (!probeEntry) return;
    for (const off of probeEntry.offs) { try { off(); } catch { /* swallow */ } }
    probeEntry.handle.dispose();
    probeEntry.panel.remove();
    probeEntry = null;
}

// ─── render ──────────────────────────────────────────────────────────────────

function el(tag: string, className?: string, text?: string): HTMLElement {
    const n = document.createElement(tag);
    if (className) n.className = className;
    if (text !== undefined) n.textContent = text;
    return n;
}

function row(label: string, value: string): HTMLElement {
    const r = el('div', 'region-row');
    r.appendChild(el('span', 'region-label', label));
    r.appendChild(el('span', 'region-value', value));
    return r;
}

function card(title: string): HTMLElement {
    const c = el('div', 'region-card');
    c.appendChild(el('div', 'region-title', title));
    return c;
}

function vec3Fmt(v: readonly [number, number, number]): string {
    return `(${v[0].toFixed(2)}, ${v[1].toFixed(2)}, ${v[2].toFixed(2)})`;
}

function statsCard(title: string, s: { medianStdLabel: string; rangeLabel: string; unitsSuffix: string }): HTMLElement {
    const c = card(title);
    c.appendChild(row('Median ± 1σ', s.medianStdLabel ? `${s.medianStdLabel}${s.unitsSuffix}` : '—'));
    c.appendChild(row('Range', s.rangeLabel ? `${s.rangeLabel}${s.unitsSuffix}` : '—'));
    return c;
}

function repaintSelection(entry: WindowEntry, id: SelectionId): void {
    const viewer = viewerRef;
    const sel: Selection | undefined = params.selections[id];
    if (!viewer || !sel) {
        entry.headerLabel.textContent = '(deleted)';
        entry.body.replaceChildren(card('Selection no longer exists'));
        return;
    }

    entry.headerLabel.textContent = sel.kind === 'box' ? `Box: ${sel.label}` : `Sphere: ${sel.label}`;

    const head = card(`${sel.kind === 'box' ? 'Box' : 'Sphere'} · ${coordSystemLabel(viewer)}`);
    if (sel.kind === 'box') {
        const b = sel as BoxSelection;
        const ex: [number, number, number] = [b.upper[0] - b.lower[0], b.upper[1] - b.lower[1], b.upper[2] - b.lower[2]];
        head.appendChild(row('Lower', vec3Fmt(b.lower)));
        head.appendChild(row('Upper', vec3Fmt(b.upper)));
        head.appendChild(row('Extent', vec3Fmt(ex)));
        head.appendChild(row('Volume', (ex[0] * ex[1] * ex[2]).toFixed(3)));
    } else {
        const s = sel as SphereSelection;
        head.appendChild(row('Center', vec3Fmt(s.center)));
        head.appendChild(row('Radius', s.radius.toFixed(3)));
        head.appendChild(row('Volume', ((4 / 3) * Math.PI * s.radius ** 3).toFixed(3)));
        head.appendChild(row('Keep', s.keep));
    }

    const within = statsCard('Field statistics (within selection)', computeMaskedFieldStats(store.state, viewer, sel));
    const global = statsCard('Field statistics (global)', computeGlobalFieldStats(store.state, viewer));

    entry.body.replaceChildren(head, within, global);
}

function repaintProbe(entry: WindowEntry): void {
    const viewer = viewerRef;
    const sel = store.state.selection;
    entry.headerLabel.textContent = 'Probe point';
    if (!viewer || !sel.sample) {
        entry.body.replaceChildren(card('No probe sample — click a point in the scene'));
        return;
    }

    const head = card(`Probe · ${coordSystemLabel(viewer)}`);
    if (sel.pos) head.appendChild(row('Position', vec3Fmt([sel.pos.x, sel.pos.y, sel.pos.z])));
    head.appendChild(row('Active field', store.state.params.fieldComponent || 'magnitude'));
    head.appendChild(row('Field value', probeValueLabel(store.state, viewer)));
    // Per-component sampling (Bx/By/Bz) isn't published to the store today —
    // updateSelectedRegion sets only the scalar value. Render components here
    // if/when the probe publisher carries them.
    const comps = sel.sample.components as Record<string, number> | undefined;
    if (comps) {
        for (const [name, value] of Object.entries(comps)) {
            head.appendChild(row(name, Number(value).toFixed(4)));
        }
    }

    const global = statsCard('Field statistics (global)', computeGlobalFieldStats(store.state, viewer));
    entry.body.replaceChildren(head, global);
}
